import React, { useEffect } from "react";
import { split } from "lodash";

import putUserQrDataHandler from "@/interface/api/putUserQrData";
import putQrDataHandler from "@/interface/api/putQrData";
import postUserQrHandler from "@/interface/api/postUserQr";
import postQrHandler from "@/interface/api/postQr";

import { QREditorDataToAPIConversor } from "@/services/ApiConversorService/QREditor";
import { removeAuthCookie } from "@/services/CookiesService";

import { downloadQrAi, downloadQrFramed } from "@/utils/qrEditorHelper";
import { QR_PAGE_INDEX } from "@/utils/qrEditorNavigatorHelper";
import { isClient } from "@/utils/browser";
import { getSiteInfo } from "@/sites";

import { QREditorContext } from "@/contexts/QREditorContext";
import { GlobalContext } from "@/contexts/GlobalContext";
import { LoadingBarContext } from "@/contexts/LoadingBarContext";

import { isUserLoggedIn } from "./auth/useLogin";
import { useApi } from "./useApi";
import { useToast } from "./useToast";

import { QRAi, QRDownloadFormat, QRStyle } from "@/types/qr";
import { QRProps } from "@/contexts/QREditorContext/types";

export const useQR = () => {
  const { toast } = useToast();

  const qrEditorContext = React.useContext(QREditorContext);
  const filesUploaderContext = React.useContext(LoadingBarContext);

  const [isLoading, setIsLoading] = React.useState(false);
  const [newData, setNewData] = React.useState(undefined);
  const [isUserLogged, setIsUserLogged] = React.useState(false);

  const { request: requestPutUserQrData, error: errorPutUserQrData } = useApi(
    putUserQrDataHandler.putUserQrData
  );
  const { request: requestPutQrData, error: errorPutQrData } = useApi(
    putQrDataHandler.putQrData
  );
  const { request: requestPostUserQrData, error: errorPostUserQrData } = useApi(
    postUserQrHandler.postUserQr
  );
  const { request: requestPostQrData, error: errorPostQrData } = useApi(
    postQrHandler.postQr
  );

  React.useEffect(() => {
    setIsUserLogged(Boolean(isUserLoggedIn()));
  }, []);

  const saveQrDataOfLoggedInUser = async () => {
    const requestQrData = qrEditorContext?.qrData.qrCodeId
      ? requestPutUserQrData
      : requestPostUserQrData;

    const data = await QREditorDataToAPIConversor.getPayload(
      qrEditorContext,
      filesUploaderContext
    );

    const qrData = await requestQrData({
      ...data.payload,
      qrCodeId: qrEditorContext?.qrData.qrCodeId,
    });
    const qrStyle: QRStyle = {
      ...data.newData.qrStyle,
      styleType: qrEditorContext?.qrData?.qrStyle?.styleType,
      controlImageAI: qrEditorContext?.qrData?.qrCodeId
        ? qrEditorContext.qrData.qrStyle.controlImageAI
        : undefined,
    };

    setNewData({
      ...qrEditorContext.qrData,
      data: data.newData.data,
      qrStyle,
    });

    return qrData;
  };

  const saveQrDataNotLoggedInUser = async () => {
    const data = await QREditorDataToAPIConversor.getPayload(
      qrEditorContext,
      filesUploaderContext
    );

    const requestQrData = qrEditorContext?.qrData.qrCodeId
      ? requestPutQrData
      : requestPostQrData;

    const qrData = await requestQrData({
      ...data.payload,
      qrCodeId: qrEditorContext?.qrData.qrCodeReference,
    });
    const qrStyle: QRStyle = {
      ...data.newData.qrStyle,
      styleType: qrEditorContext?.qrData?.qrStyle?.styleType,
      controlImageAI: qrEditorContext?.qrData?.qrCodeId
        ? qrEditorContext.qrData.qrStyle.controlImageAI
        : undefined,
    };

    setNewData({
      ...qrEditorContext.qrData,
      data: data.newData.data,
      qrStyle,
    });

    return qrData;
  };

  const requestQR = async () => {
    let newData = qrEditorContext.qrData;
    const step = qrEditorContext.qrData.currentPageIndex;

    if (
      step === QR_PAGE_INDEX.ADD_CONTENT ||
      step === QR_PAGE_INDEX.DESIGN_QR
    ) {
      setIsLoading(true);

      if (isUserLogged) {
        const qrData = await saveQrDataOfLoggedInUser();
        newData = (await qrData?.data) as QRProps<undefined>;
      } else {
        const qrData = await saveQrDataNotLoggedInUser();
        newData = (await qrData?.data) as QRProps<undefined>;
      }
      setIsLoading(false);
      return newData;
    }
  };

  useEffect(() => {
    if (
      errorPostQrData ||
      errorPostUserQrData ||
      errorPutUserQrData ||
      errorPutQrData
    ) {
      if (
        errorPostQrData?.status === 401 ||
        errorPostUserQrData?.status === 401 ||
        errorPutUserQrData?.status === 401 ||
        errorPutQrData?.status === 401
      ) {
        removeAuthCookie();
        requestQR();
      } else {
        toast({ message: "genericError", type: "error" });
      }
      setIsLoading(false);
    }
  }, [
    errorPostQrData,
    errorPostUserQrData,
    errorPutUserQrData,
    errorPutQrData,
  ]);

  return {
    requestQR,
    isLoading,
    newData,
  };
};

export const useQRStyle = (
  type?: "svg" | "canvas",
  size?: number,
  errorCorrectionLevel?: "L" | "M" | "Q" | "H"
) => {
  const { context, setContext } = React.useContext(GlobalContext);

  const { domainName } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN);
  const ref = React.useRef(null);

  const [qrCode, setQrCode] = React.useState(undefined);
  const [url, setUrl] = React.useState(`https://${domainName}`);
  const download = (
    nameOfFile: string,
    extension: QRDownloadFormat,
    data?: { style: QRStyle; qrUrl?: string; ai?: QRAi }
  ) => {
    const frameStyle = data?.style?.frameStyle;

    if (frameStyle && frameStyle !== "none") {
      downloadQrFramed(qrCode, data.style, extension, nameOfFile);
    } else if (data.ai?.selectedImageUrl) {
      downloadQrAi(data, data.ai.selectedImageUrl, extension, nameOfFile);
    } else {
      qrCode?.download({ name: nameOfFile, extension });
    }
  };

  const getRawData = async () => {
    const rawData = await qrCode?.getRawData("png");
    return rawData;
  };

  const updateQRCode = (data: {
    qrStyle: QRStyle;
    qrUrl?: string;
    margin?: number;
  }) => {
    const dotsType = data?.qrStyle.dotsStyle;
    const cornerDotType = split(data?.qrStyle.cornerStyle, "-")[1];
    const cornerSquareType = split(data?.qrStyle.cornerStyle, "-")[0];

    qrCode?.update({
      data: data?.qrUrl || url,
      image: data?.qrStyle.logoUrl,
      imageOptions: {
        margin: 0,
        imageSize: 0.4,
      },
      cornersDotOptions: {
        color: data?.qrStyle?.cornerBackgroundColor,
        type: cornerDotType,
      },
      cornersSquareOptions: {
        color: data?.qrStyle?.cornerBorderColor,
        type:
          cornerSquareType && cornerSquareType === "rounded"
            ? "extra-rounded"
            : cornerSquareType,
      },
      dotsOptions: {
        color: data?.qrStyle?.dotsColor,
        type: dotsType,
      },
      backgroundOptions: {
        color: data?.qrStyle?.backgroundColor,
      },
      margin: data?.margin || 0,
    });

    qrCode?.append(ref.current);
  };

  React.useEffect(() => {
    if (!isClient()) return;

    const QRCodeStyling = require("qr-code-styling");
    const qr = new QRCodeStyling({
      width: size || 300,
      height: size || 300,
      type: type || "canvas",
      dotsOptions: {
        color: "#000000",
        type: "square",
      },
      imageOptions: {
        crossOrigin: "anonymous",
        margin: 20,
      },
      errorCorrectionLevel: errorCorrectionLevel || "Q",
    });
    qr.append(ref.current);

    setQrCode(qr);
  }, []);

  React.useEffect(() => {
    if (qrCode) {
      setContext({ ...context, qrCode });
    }
  }, [qrCode]);

  return {
    updateQRCode,
    download: (nameOfFile: string, extension: QRDownloadFormat, data?) =>
      download(nameOfFile, extension, data),
    setUrl,
    getRawData,
    qrCode,
    url,
    ref,
  };
};
